// The teacher's corrections at Review -> PATCH /submissions/{id}/answers/{serial}.
//
// Extraction is allowed to fail soft — the Converting step says so in as many
// words — on the promise that the teacher can type the text in at Review. That
// promise is only kept if what they type reaches the answer the run marks, so
// this is the resolution from the id the wizard routes by to the resource that
// stores the response.
//
// Derivation only, no I/O: the same split submissionBatch.js and feedbackRun.js
// keep, so the saves can be tested without a server.

const answerOf = (submission) => submission?.answers?.[0];

/** The answer's own serial, not an assumption about which one it is. */
const serialNumberOf = (submission) => answerOf(submission)?.serialNumber ?? '1';

/**
 * Same rule kickOptions applies in draftSubmissions: a batched answer that carries
 * pages is created at generation 1, so the zero default every other caller relies
 * on makes this PATCH 409 instead of saving.
 */
const generationOf = (submission) =>
  Number(answerOf(submission)?.answer?.attachmentGeneration) || 0;

// The roster is the server's and the edit map is the wizard's, so the two sides of
// this lookup are keyed independently — String() is what makes them meet.
const submissionsByStudentId = (submissions) =>
  new Map((submissions || []).map((s) => [String(s?.studentId), s]));

const toSave = (submission, studentId, text) => ({
  submissionId: submission.id,
  studentId,
  serialNumber: serialNumberOf(submission),
  answer: { answer: text, highlights: [] },
  options: { attachmentGeneration: generationOf(submission) },
});

/** Anything to save at all — an emptied response counts, it is a deletion. */
export const hasReviewEdits = (edits) => Object.keys(edits || {}).length > 0;

/**
 * @param {Object} edits - studentId -> the text now in the Review editor
 * @param {Array} submissions - the assignment's submissions, as the server has them
 * @returns {Array} one PATCH per edited student. A student the roster cannot name
 * has no submission to correct, so their edit is dropped rather than sent at
 * `undefined`.
 */
export const answerSavesFor = (edits = {}, submissions = []) => {
  const submissionFor = submissionsByStudentId(submissions);
  return Object.entries(edits)
    .map(([studentId, text]) => [submissionFor.get(String(studentId)), studentId, text])
    .filter(([submission]) => submission?.id)
    .map(([submission, studentId, text]) => toSave(submission, studentId, text));
};

const nameOf = (students, studentId) =>
  (students || []).find((student) => String(student?.id) === String(studentId))
    ?.name || null;

const namesFor = (saves, students) =>
  saves.map((save) => nameOf(students, save.studentId)).filter(Boolean);

/**
 * Which corrections did not reach the server. Named where the roster allows it —
 * "your changes to Ada and Grace" is actionable in a way "2 responses" is not.
 */
export const unsavedEditsMessage = (failed = [], students = []) => {
  const names = namesFor(failed, students);
  const who = names.length ? names.join(', ') : `${failed.length} response(s)`;
  return `Your changes to ${who} could not be saved, so feedback was not requested. Please try again.`;
};

// ---------------------------------------------------------------------------
// What Review shows, and what it saves
// ---------------------------------------------------------------------------

const pageTextOf = (page) => {
  if (page?.source === 'manual') return page.content || '';
  return page?.extractedText || page?.content || '';
};

/**
 * Extraction is per-ANSWER, and mapSubmissionsOntoPages stamps that one answer
 * onto every page routed to the student — so joining pages naively shows a
 * two-page scan its own text twice. Distinct texts only: a scanned response
 * collapses back to itself, while genuinely different typed pages still join.
 *
 * This is the value the Review editor opens on AND the value saved back to the
 * submission, so both have to read it through here or the save doubles what the
 * screen showed.
 */
export const responseTextOf = (pages) =>
  [...new Set((pages || []).map(pageTextOf).filter(Boolean))].join('\n\n');

const textForStudent = (pageIds, pageById) =>
  responseTextOf((pageIds || []).map((pageId) => pageById.get(pageId)));

const indexPagesById = (pages) =>
  new Map((pages || []).map((page) => [page.id, page]));

/**
 * `!== undefined`, not a truthiness check: an emptied response is a deletion the
 * teacher meant, and falling through to the extracted text would silently undo it.
 */
const wasEdited = (edits, studentId) => edits?.[studentId] !== undefined;

/**
 * Which students' text is saved back to the answers at Continue.
 *
 * FE-BU-EXTRACT: extracted text is saved even when the teacher does not edit it,
 * so a reopened draft does not show empty responses for files that read fine.
 *
 * The settled gate is what makes that safe now that Converting leaves at the
 * FIRST student to extract: the teacher can reach Continue with others still
 * converting, and for those the client holds nothing the server does not already
 * have. Writing it anyway races the extraction worker, and the
 * attachmentGeneration answerSavesFor carries may already have moved — which is a
 * 409 rather than a save.
 *
 * @param {Object} params
 * @param {Object} params.edits - studentId -> the text now in the Review editor
 * @param {Object} params.assignments - studentId -> pageIds
 * @param {Array} params.pages - the wizard's pages
 * @param {Set<string>} params.settledStudentIds - students whose extraction has
 *   reported. Empty before the first status read, so an unedited student is
 *   skipped rather than saved from a page list that is still filling.
 * @returns {Object} studentId -> text to save
 */
export const textsToPersist = ({
  edits = {},
  assignments = {},
  pages = [],
  settledStudentIds = new Set(),
}) => {
  const pageById = indexPagesById(pages);
  // An edit always saves, empty or not. Extracted text only saves once the
  // server has reported on that student, and only if there is any.
  const saveFor = (studentId, pageIds) => {
    if (wasEdited(edits, studentId)) return edits[studentId];
    if (!settledStudentIds.has(String(studentId))) return null;
    return textForStudent(pageIds, pageById) || null;
  };
  return Object.fromEntries(
    Object.entries(assignments)
      .map(([studentId, pageIds]) => [studentId, saveFor(studentId, pageIds)])
      .filter(([, text]) => text !== null),
  );
};
